import "twin.macro";

import { useEffect, useRef } from "react";
import { Link, useRouteError } from "react-router";

import { Button } from "@mui/material";

import { Contained } from "@components/StyledComponents";
import DocTitle from "@utility/DocTitle";

import FourOhFour from "./FourOhFour";

type ErrorPageProps = {
  error?: Error;
  errorReset?: () => void;
};

const ErrorPage = ({ error: providedError, errorReset }: ErrorPageProps) => {
  const routeError = useRouteError() as any;
  const error = providedError ?? routeError;
  const resetRef = useRef(false);

  const handleManualReset = () => {
    resetRef.current = true;
    errorReset?.();
  };

  // Reset the error if the user navigates away from the error page
  useEffect(() => {
    return () => {
      // Check if the error has been reset by the user
      if (!resetRef.current) {
        errorReset?.();
      }
    };
  }, [errorReset]);

  if (error?.status === 404) return <FourOhFour />;

  return (
    <>
      <DocTitle title={"Error"} />
      <Contained>
        <h1 tw="text-6xl text-neutral-300 font-bold">error.</h1>
        <h2 tw="text-4xl text-neutral-600 font-bold">
          Something unexpected happened.
        </h2>
        <p tw="text-lg max-w-prose mt-2">
          If this error persists, please contact support and we'll get it
          resolved.
        </p>
        {error && (
          <pre tw="mt-2 p-3 rounded-lg bg-neutral-200 shadow-inner whitespace-pre-line">
            {error?.message ?? error}
          </pre>
        )}
        <div tw="flex gap-2">
          {errorReset && (
            <Button tw="mt-6" variant="contained" onClick={handleManualReset}>
              Try again
            </Button>
          )}
          <Button tw="mt-6" variant="contained" component={Link} to={"/"}>
            Go Back
          </Button>
        </div>
      </Contained>
    </>
  );
};

export default ErrorPage;
